import React, {useEffect, useRef, useState} from "react";
import L, {LatLngExpression, LeafletMouseEvent, Map} from 'leaflet'
import 'leaflet/dist/leaflet.css';
import './MapComponent.css'
import {HomePlant} from "../../Services/typescript-fetch-client";
import {NavLink} from "react-router-dom";
import {MapContainer, Marker, Popup, ScaleControl, TileLayer, useMapEvent, ZoomControl} from "react-leaflet";
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import {faCircleCheck, faCircle} from '@fortawesome/free-regular-svg-icons'
import decimalRound from "../../Utils/decimalRound";
import formatNumber from "../../Utils/formatNumber";
import useWindowDimensions from "../ui-components/useWindowDimensions";
import { ReactComponent as WindSvg} from  '../ui-components/icons/Eolico.svg'
import { ReactComponent as BioSvg} from  '../ui-components/icons/Bioenergie.svg'
import { ReactComponent as GasSvg} from  '../ui-components/icons/Gas.svg'
import { ccgtIconToString, windIconToString, bioIconToString } from "../ui-components/icons";

type Props = {
    homePlants: HomePlant[]
}

const MapComponent = (props: Props) => {
    const {height, width} = useWindowDimensions();
    const [zoom, setZoom] = useState<number>(selectStartZoomLevel(height, width));
    const mapRef = useRef<Map>(null);

    const plantActiveColor = '#97d700';
    const plantActiveHoverColor = '#193496';
    const plantStoppedColor = '#999999';
    const plantStoppedHoverColor = '#bfbfbf';

    useEffect(() => {
        let time = setTimeout(() => {//Need timeout because resize event fires before sizes actually change, but we need them to be already changed
            setZoom(selectStartZoomLevel(height, width));
            if(mapRef.current != null) {
                mapRef.current.invalidateSize({
                    animate: false,
                    pan: true,
                    duration: 0
                });
            }
        }, 500);
        
        return () => {
            clearTimeout(time);
        }
    }, [height, width]);
    

    function getPlaceholderIcon(color: string){
        return '<?xml version="1.0" encoding="UTF-8" standalone="no"?><svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" style="isolation:isolate" viewBox="0 0 391 520" width="391pt" height="520pt"><defs><clipPath id="_clipPath_ps2E8v8F8c2EgNs3Z4jbE2qfk7lwepr5"><rect width="391" height="520"/></clipPath></defs><g clip-path="url(#_clipPath_ps2E8v8F8c2EgNs3Z4jbE2qfk7lwepr5)"><clipPath id="_clipPath_ahUMXvpKhbblqqoQpGLTCiovrEBJwtwS"><rect x="0" y="0" width="391" height="520" transform="matrix(1,0,0,1,0,0)" fill="#000000"/></clipPath><g clip-path="url(#_clipPath_ahUMXvpKhbblqqoQpGLTCiovrEBJwtwS)"><g><defs><filter id="vsQ9nWMnklyauxIjWkrG3aTuPoeukReK" x="-200%" y="-200%" width="400%" height="400%" filterUnits="objectBoundingBox" color-interpolation-filters="sRGB"><feGaussianBlur xmlns="http://www.w3.org/2000/svg" in="SourceGraphic" stdDeviation="3.4348872502712227"/><feOffset xmlns="http://www.w3.org/2000/svg" dx="6" dy="6" result="pf_100_offsetBlur"/><feFlood xmlns="http://www.w3.org/2000/svg" flood-color="#000000" flood-opacity="0.65"/><feComposite xmlns="http://www.w3.org/2000/svg" in2="pf_100_offsetBlur" operator="in" result="pf_100_dropShadow"/><feBlend xmlns="http://www.w3.org/2000/svg" in="SourceGraphic" in2="pf_100_dropShadow" mode="normal"/></filter></defs><g filter="url(#vsQ9nWMnklyauxIjWkrG3aTuPoeukReK)"><path d=" M 170.093 501.67 C 26.629 291.031 0 269.413 0 192 C 0 85.961 84.876 0 189.576 0 C 294.276 0 379.152 85.961 379.152 192 C 379.152 269.413 352.522 291.031 209.059 501.67 C 199.644 515.444 179.507 515.443 170.093 501.67 Z  M 189.576 272 C 233.201 272 268.566 236.183 268.566 192 C 268.566 147.817 233.201 112 189.576 112 C 145.951 112 110.586 147.817 110.586 192 C 110.586 236.183 145.951 272 189.576 272 Z " fill="' + color + '"/></g></g></g></g></svg>';
    }

    function getWindIcon(color: string){
        const dynamicIcon = windIconToString.replaceAll('#a9a9a9', color)
        return '<?xml version="1.0" encoding="UTF-8" standalone="no"?>' + dynamicIcon; 
    }

    function getCcgtIcon(color: string){ 
    const dynamicIcon = ccgtIconToString.replaceAll('#a9a9a9', color)
       return '<?xml version="1.0" encoding="UTF-8" standalone="no"?>' + dynamicIcon;  
    }

    function getBioIcon(color: string){ 
        const dynamicIcon = bioIconToString.replaceAll('#a9a9a9', color)
           return '<?xml version="1.0" encoding="UTF-8" standalone="no"?>' + dynamicIcon;  
        }

    const windActiveIcon = L.icon({
        iconUrl: 'data:image/svg+xml;base64,' + btoa(getWindIcon(plantActiveColor)),
        iconSize: [24, 30],
        iconAnchor: [15, 20],
        className: "plantActive"
    });

    const ccgtActiveIcon = L.icon({
        iconUrl: 'data:image/svg+xml;base64,' + btoa(getCcgtIcon(plantActiveColor)),
        iconSize: [24, 30],
        iconAnchor: [15, 20],
        className: "plantActive"
    });

    const bioActiveIcon = L.icon({
        iconUrl: 'data:image/svg+xml;base64,' + btoa(getBioIcon(plantActiveColor)),
        iconSize: [24, 30],
        iconAnchor: [15, 20],
        className: "plantActive"
    });

    const windActiveHoverIcon = L.icon({
        iconUrl: 'data:image/svg+xml;base64,' + btoa(getWindIcon(plantActiveHoverColor)),
        iconSize: [24, 30],
        iconAnchor: [15, 20],
        className: "plantActiveHover"
    });

    const ccgtActiveHoverIcon = L.icon({
        iconUrl: 'data:image/svg+xml;base64,' + btoa(getCcgtIcon(plantActiveHoverColor)),
        iconSize: [24, 30],
        iconAnchor: [15, 20],
        className: "plantActiveHover"
    });

    const bioActiveHoverIcon = L.icon({
        iconUrl: 'data:image/svg+xml;base64,' + btoa(getBioIcon(plantActiveHoverColor)),
        iconSize: [24, 30],
        iconAnchor: [15, 20],
        className: "plantActiveHover"
    });

    const windStoppedIcon = L.icon({
        iconUrl: 'data:image/svg+xml;base64,' + btoa(getWindIcon(plantStoppedColor)),
        iconSize: [24, 30],
        iconAnchor: [15, 20],
        className: "plantStopped"
    });

    const ccgtStoppedIcon = L.icon({
        iconUrl: 'data:image/svg+xml;base64,' + btoa(getCcgtIcon(plantStoppedColor)),
        iconSize: [24, 30],
        iconAnchor: [15, 20],
        className: "plantStopped"
    });

    const bioStoppedIcon = L.icon({
        iconUrl: 'data:image/svg+xml;base64,' + btoa(getBioIcon(plantStoppedColor)),
        iconSize: [24, 30],
        iconAnchor: [15, 20],
        className: "plantStopped"
    });

    const windStoppedHoverIcon = L.icon({
        iconUrl: 'data:image/svg+xml;base64,' + btoa(getWindIcon(plantStoppedHoverColor)),
        iconSize: [24, 30],
        iconAnchor: [15, 20],
        className: "plantStoppedHover"
    });

    const ccgtStoppedHoverIcon = L.icon({
        iconUrl: 'data:image/svg+xml;base64,' + btoa(getCcgtIcon(plantStoppedHoverColor)),
        iconSize: [24, 30],
        iconAnchor: [15, 20],
        className: "plantStoppedHover"
    });

    const bioStoppedHoverIcon = L.icon({
        iconUrl: 'data:image/svg+xml;base64,' + btoa(getBioIcon(plantStoppedHoverColor)),
        iconSize: [24, 30],
        iconAnchor: [15, 20],
        className: "plantStoppedHover"
    });

    function selectIcon(iconType: HomePlant.TypeEnum, status: boolean) {
        let icon: L.Icon<L.IconOptions> = L.icon({
            iconUrl: 'data:image/svg+xml;base64,' + btoa(getPlaceholderIcon(plantStoppedHoverColor)),
            iconSize: [24, 30],
            iconAnchor: [15, 20],
            className: "plantStoppedHover"
        });

        switch (iconType) {
            case "wind":
                if (status) {
                    icon = windActiveIcon
                } else {
                    icon = windStoppedIcon
                }
                break;
            case "ccgt":
                if (status) {
                    icon = ccgtActiveIcon
                } else {
                    icon = ccgtStoppedIcon
                }
                break;
            case "bioenergy":
                if (status) {
                    icon = bioActiveIcon
                } else {
                    icon = bioStoppedIcon
                }
                break;
            default:
                break;
        }

        return icon;
    }

    function iconMouseOver(e: LeafletMouseEvent){
        switch (e.sourceTarget.getIcon()) {
            case windStoppedIcon:
                e.sourceTarget.setIcon(windStoppedHoverIcon);
                break;
            case ccgtStoppedIcon:
                e.sourceTarget.setIcon(ccgtStoppedHoverIcon);
                break;
            case bioStoppedIcon:
                e.sourceTarget.setIcon(bioStoppedHoverIcon);
                break;
            case windActiveIcon:
                e.sourceTarget.setIcon(windActiveHoverIcon);
                break;
            case ccgtActiveIcon:
                e.sourceTarget.setIcon(ccgtActiveHoverIcon);
                break;
            case bioActiveIcon:
                e.sourceTarget.setIcon(bioActiveHoverIcon);
                break;
            default:
                break;
        }
    }

    function iconMouseout(e: LeafletMouseEvent){
        switch (e.sourceTarget.getIcon()) {
            case windStoppedHoverIcon:
                e.sourceTarget.setIcon(windStoppedIcon);
                break;
            case ccgtStoppedHoverIcon:
                e.sourceTarget.setIcon(ccgtStoppedIcon);
                break;
            case bioStoppedHoverIcon:
                e.sourceTarget.setIcon(bioStoppedIcon);
                break;
            case windActiveHoverIcon:
                e.sourceTarget.setIcon(windActiveIcon);
                break;
            case ccgtActiveHoverIcon:
                e.sourceTarget.setIcon(ccgtActiveIcon);
                break;
            case bioActiveHoverIcon:
                e.sourceTarget.setIcon(bioActiveIcon);
                break;
            default:
                break;
        }
    }

    function selectStartZoomLevel(h: number, w: number){
        let ratio = w / h;

        if(ratio <= 0.45) {//heigher then larger
            return 5;
        } else if(ratio <= 0.6 || (ratio === 0.75 && width === 540)){//almost square landscape
            return 5.5;
        } else if(ratio <= 1.4){//almost square landscape
            return 6;
        }
        else /*if(ratio > 1.4)*/ {//larger than heigher
            if(w > 1400){
                return 6;
            }
            else{
                return 5.5;
            }
        }
    }

    let plantsPlaces : object = {};
    if(props.homePlants) {
        plantsPlaces = props.homePlants.reduce(function (r, a) {
                r[a.lat + "_" + a.lng] = r[a.lat + "_" + a.lng] || [];
                r[a.lat + "_" + a.lng].push(a);
                return r;
            }, Object.create(null));
    }

    const getCoordinates = () => {
        let coordinates: LatLngExpression | undefined = [41.890196167312055, 12.492341923687258]
        if (height < 740) {
            console.log("CIOAOOSDOW");
            coordinates = [41, 12] 
        } else if (height > 1800) {
            coordinates = [38.480392438619326, 14.146368088980623]
        } else {
            coordinates = [41.890196167312055, 12.492341923687258]
        }
        console.log("COORDINATES: ", coordinates);

        return coordinates
    }
    console.log("HEIGHT_0: ", height);
    console.log("ZOOM_0: ", zoom);

    
    return (
        <MapContainer id="map" center={getCoordinates()} ref={mapRef} trackResize={true}
                      zoomSnap={0} zoomDelta={0.3} zoomControl={false} zoom={zoom} 
                      maxZoom={18} minZoom={2}>
            <TileLayer
                attribution='&copy; <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" 
            />
            <ZoomControl position="topleft" />
            <ScaleControl position="bottomleft" metric imperial={false} />
            {
                Object.keys(plantsPlaces).map((place, index) => <Marker key={place} position={[plantsPlaces[place][0].lat!, plantsPlaces[place][0].lng!]} 
                icon={selectIcon(plantsPlaces[place][0].type, Boolean(Math.max(...plantsPlaces[place].map(pl => Number((pl as HomePlant).onProd)))))} 
                    eventHandlers={{mouseout: (e) => iconMouseout(e), mouseover: (e) => iconMouseOver(e)}}>
                    <Popup offset={L.point(0,-20)}>
                        {(plantsPlaces[place] as HomePlant[]).map(p => 
                            <div key={p.id} className={"mapToolip" + (plantsPlaces[place].length > 1 ? " multiple" : "")}>
                                <div className="mapTooltipHeader flex flex-row align-items-center justify-content-between">
                                    <div className="theLeft">
                                        {p.type! === HomePlant.TypeEnum.Ccgt ? <GasSvg /> :
                                            p.type! === HomePlant.TypeEnum.Wind ? <WindSvg /> : <BioSvg />
                                        } <NavLink to={"/" +p.type + "/" + p.plantName } className="plantPopupTitle">{p.type === HomePlant.TypeEnum.Wind ? `${p.plantName} (${p.province?.name})` : `${p.plantName}`}</NavLink>
                                    </div>
                                    <div className={"theRight" + (p.onProd ? ' active': '')}>
                                        {p.onProd ? "In Marcia" : "Non in Marcia"}
                                        <FontAwesomeIcon icon={p.onProd ? faCircleCheck : faCircle} />
                                    </div>
                                </div>
                                <div>
                                    <div className="flex justify-content-between flex-nowrap tooltipKpi">
                                        <div className="flex align-items-center justify-content-start tooltipKpiLabel">EE prodotta da inizio giornata</div>
                                        <div className="flex align-items-center justify-content-end tooltipKpiValue">{formatNumber(decimalRound(p.eeToday ?? 0, 0))} MWh</div>
                                    </div>
                                    <div className="flex justify-content-between flex-nowrap tooltipKpi">
                                        <div className="flex align-items-center justify-content-start tooltipKpiLabel">Produzione attesa per oggi</div>
                                        <div className="flex align-items-center justify-content-end tooltipKpiValue">{formatNumber(decimalRound(p.prodExpectedToday ?? 0, 0))} MWh</div>
                                    </div>
                                </div>
                                <NavLink to={"/" +p.type + "/" + p.plantName } className="plantDetailBtn">Dettaglio Impianto</NavLink>
                            </div>)}
                    </Popup>
                </Marker>)
            }
        </MapContainer>
    );
}

export default MapComponent;